import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { VendorPlan } from '../../superadmin/our-vendors/models/vendor.model';
import { money } from './rate-card';

export interface OfferRow {
  plan: string;
  monthlyPrice: number | '';
  annualPrice: number | '';
}

export interface OfferDraft {
  items: OfferRow[];
  currency: string;
  /** '' = the plan's own setup fee; 0 waives it. */
  integrationFee: number | '';
  note: string;
}

/** Plans that can be offered: sold plans, not the trial or the retired Free. */
export const offerablePlans = (plans: VendorPlan[]) =>
  plans.filter((p) => p.pricingType !== 'trial' && p.pricingType !== 'free' && p.slug !== 'free' && p.slug !== 'trial');

/** A first offer: the cheapest public plan at its list price — the approver adjusts it. */
export function defaultOffer(plans: VendorPlan[]): OfferDraft {
  const sellable = offerablePlans(plans).filter((p) => p.isPublic !== false && Number(p.price) > 0);
  const first = sellable.sort((a, b) => Number(a.price) - Number(b.price))[0];
  return {
    items: first ? [{ plan: first._id, monthlyPrice: Number(first.price), annualPrice: Number(first.annualPrice) || '' }] : [],
    currency: first?.currency || 'usd',
    integrationFee: '',
    note: '',
  };
}

/** What the API takes: empty rows dropped, blanks as null. */
export function offerPayload(d: OfferDraft) {
  const items = d.items
    .filter((r) => r.plan && Number(r.monthlyPrice) > 0)
    .map((r) => ({ plan: r.plan, monthlyPrice: Number(r.monthlyPrice), annualPrice: r.annualPrice === '' ? null : Number(r.annualPrice) }));
  if (!items.length) return null;
  return {
    items,
    currency: d.currency || 'usd',
    integrationFee: d.integrationFee === '' ? null : Number(d.integrationFee),
    note: d.note || undefined,
  };
}

export function offerFromApi(o: any): OfferDraft | null {
  if (!o?.items?.length) return null;
  return {
    items: o.items.map((i: any) => ({ plan: String(i.plan), monthlyPrice: i.monthlyPrice ?? '', annualPrice: i.annualPrice ?? '' })),
    currency: o.currency || 'usd',
    integrationFee: o.integrationFee ?? '',
    note: o.note || '',
  };
}

/**
 * The prices one vendor will pay (docs/pricing-v2.md, Offers): one or more
 * plans, each with its own monthly and optional yearly price, and the setup
 * fee. List prices are shown beside each row as the reference.
 */
export function OfferEditor({ plans, value, onChange }: { plans: VendorPlan[]; value: OfferDraft; onChange: (d: OfferDraft) => void }) {
  const { t } = useTranslation();
  const choices = offerablePlans(plans);
  const set = (patch: Partial<OfferDraft>) => onChange({ ...value, ...patch });
  const setRow = (i: number, patch: Partial<OfferRow>) =>
    set({ items: value.items.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const used = new Set(value.items.map((r) => r.plan));
  const listOf = (id: string) => choices.find((p) => p._id === id);

  return (
    <div className="flex flex-col gap-4" data-testid="offer-editor">
      <div className="flex flex-col gap-3">
        {value.items.map((row, i) => {
          const list = listOf(row.plan);
          const monthly = Number(row.monthlyPrice) || 0;
          const below = list && Number(list.price) > 0 && monthly > 0 && monthly < Number(list.price);
          return (
            <div key={i} className="grid grid-cols-1 items-end gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_auto]" data-testid={`offer-row-${i}`}>
              <div className="flex min-w-0 flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">{t('PRICING.OFFER.PLAN')}</Label>
                <Select value={row.plan} onValueChange={(v) => {
                  const p = listOf(v);
                  setRow(i, { plan: v, monthlyPrice: row.monthlyPrice || Number(p?.price) || '', annualPrice: row.annualPrice || Number(p?.annualPrice) || '' });
                }}>
                  <SelectTrigger className="w-full" data-testid={`offer-plan-${i}`}><SelectValue placeholder={t('PRICING.OFFER.PICK_PLAN')} /></SelectTrigger>
                  <SelectContent className="z-[10001]">
                    {choices.map((p) => (
                      <SelectItem key={p._id} value={p._id} disabled={used.has(p._id) && p._id !== row.plan}>
                        {p.name}{Number(p.price) > 0 ? ` · ${t('PRICING.OFFER.LIST', { price: money(Number(p.price), p.currency) })}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">{t('PRICING.OFFER.MONTHLY')}</Label>
                <Input type="number" min={1} className="tabular-nums" value={row.monthlyPrice} aria-invalid={!row.monthlyPrice || undefined}
                  onChange={(e) => setRow(i, { monthlyPrice: e.target.value === '' ? '' : Number(e.target.value) })} data-testid={`offer-monthly-${i}`} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs text-muted-foreground">{t('PRICING.OFFER.YEARLY')}</Label>
                <Input type="number" min={1} className="tabular-nums" value={row.annualPrice} placeholder={monthly ? String(monthly * 10) : ''}
                  onChange={(e) => setRow(i, { annualPrice: e.target.value === '' ? '' : Number(e.target.value) })} data-testid={`offer-yearly-${i}`} />
              </div>
              <Button variant="ghost" size="icon" aria-label={t('PRICING.OFFER.REMOVE')} disabled={value.items.length <= 1}
                onClick={() => set({ items: value.items.filter((_, j) => j !== i) })}>
                <X className="size-4" />
              </Button>
              {below && (
                <p className="text-xs text-muted-foreground sm:col-span-4">{t('PRICING.OFFER.BELOW_LIST', { price: money(Number(list!.price), list!.currency) })}</p>
              )}
            </div>
          );
        })}
        {value.items.length < choices.length && (
          <Button variant="outline" size="sm" className="self-start" data-testid="offer-add"
            onClick={() => set({ items: [...value.items, { plan: '', monthlyPrice: '', annualPrice: '' }] })}>
            <Plus className="size-3.5" />
            {t('PRICING.OFFER.ADD')}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs text-muted-foreground">{t('PLANS.ADMIN.FORM.CURRENCY')}</Label>
          <Input className="uppercase" value={value.currency} onChange={(e) => set({ currency: e.target.value.toLowerCase() })} />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label className="text-xs text-muted-foreground">{t('PRICING.OFFER.SETUP_FEE')}</Label>
          <Input type="number" min={0} className="tabular-nums" value={value.integrationFee} placeholder={t('PRICING.OFFER.SETUP_FEE_DEFAULT')}
            onChange={(e) => set({ integrationFee: e.target.value === '' ? '' : Number(e.target.value) })} />
        </div>
      </div>
    </div>
  );
}
